import React, { useState } from 'react';
import { useModelStore } from '../../../store/useModelStore';
import { useUIStore } from '../../../store/useUIStore';

interface StaticAnalysisProps {
  onAnalysisRun?: (type: string) => void;
}

export const StaticAnalysis: React.FC<StaticAnalysisProps> = ({ onAnalysisRun }) => {
  const [analysisOptions, setAnalysisOptions] = useState({
    includeGeometricNonlinearity: false,
    includeMaterialNonlinearity: false,
    includePDelta: false,
    iterationLimit: 100,
    convergenceTolerance: 0.001
  });

  const { showNotification } = useUIStore();
  const { elements, nodes } = useModelStore();

  const handleRunAnalysis = () => {
    if (elements.length === 0 || nodes.length === 0) {
      showNotification('warning', 'No Model Data', 'Please create a model before running analysis');
      return;
    }

    showNotification('info', 'Analysis Started', 'Static linear analysis is running...');
    onAnalysisRun?.('static');
    
    // Simulate analysis
    setTimeout(() => {
      showNotification('success', 'Analysis Complete', 'Static analysis completed successfully');
    }, 2000);
  };

  return (
    <div className="static-analysis-panel">
      <div className="panel-header">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Static Analysis</h3>
      </div>
      
      <div className="panel-content space-y-6">
        <div className="analysis-options">
          <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Analysis Options</h4>
          
          <div className="space-y-3">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={analysisOptions.includeGeometricNonlinearity}
                onChange={(e) => setAnalysisOptions({
                  ...analysisOptions,
                  includeGeometricNonlinearity: e.target.checked
                })}
                className="rounded"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Include Geometric Nonlinearity
              </span>
            </label>
            
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={analysisOptions.includeMaterialNonlinearity}
                onChange={(e) => setAnalysisOptions({
                  ...analysisOptions,
                  includeMaterialNonlinearity: e.target.checked
                })}
                className="rounded"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Include Material Nonlinearity
              </span>
            </label>
            
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={analysisOptions.includePDelta}
                onChange={(e) => setAnalysisOptions({
                  ...analysisOptions,
                  includePDelta: e.target.checked
                })}
                className="rounded"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Include P-Delta Effects
              </span>
            </label>
          </div>
        </div>

        <div className="convergence-settings">
          <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Convergence Settings</h4>
          
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Maximum Iterations
              </label>
              <input
                type="number"
                value={analysisOptions.iterationLimit}
                onChange={(e) => setAnalysisOptions({
                  ...analysisOptions,
                  iterationLimit: parseInt(e.target.value)
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="1"
                max="1000"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Convergence Tolerance
              </label>
              <input
                type="number"
                value={analysisOptions.convergenceTolerance}
                onChange={(e) => setAnalysisOptions({
                  ...analysisOptions,
                  convergenceTolerance: parseFloat(e.target.value)
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0.0001"
                max="0.1"
                step="0.0001"
              />
            </div>
          </div>
        </div>

        <div className="analysis-actions">
          <button
            onClick={handleRunAnalysis}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
          >
            Run Static Analysis
          </button>
        </div>
      </div>
    </div>
  );
};

export default StaticAnalysis;
